import type * as React from 'react';
import { Suspense, useEffect, useState } from 'react';
import ErrorBoundary from '../react/components/ErrorBoundary';
import ClippyTrigger from '../react/features/Clippy/components/Trigger/lazy';
import { AppAreaSizeProvider } from '../react/hooks/useAreaSize';
import { CompanyDesignProvider } from '../react/services/CompanyDesignProvider';
import * as keyEventStackService from '../react/services/KeyEventStack';
import Router from '../routing/Router';
import AppRoutes from '../routing/Routes';
import history, { globalClickHandler } from '../routing/history';
import AppShell from './AppShell';

const useGlobalLinkClickListener = () => {
    useEffect(() => {
        document.addEventListener('click', globalClickHandler);
        return () => document.removeEventListener('click', globalClickHandler);
    }, []);
};

const useNavigationToggleState = () => {
    const [showNavigation, setShowNavigation] = useState(false);

    useEffect(() => {
        if (showNavigation) {
            const className = 'main-navigation-open';
            document.body.classList.add(className);

            const keyEvent = {
                onKey: (event: unknown) => {
                    if (keyEventStackService.isEsc(event)) {
                        setShowNavigation(false);
                        return false;
                    }
                },
            };
            keyEventStackService.register(keyEvent);

            const unlisten = history?.listen(() => setShowNavigation(false));

            return () => {
                document.body.classList.remove(className);
                keyEventStackService.deregister(keyEvent);
                unlisten?.();
            };
        }
    }, [showNavigation]);

    return { showNavigation, setShowNavigation };
};

const App: React.FC = () => {
    useGlobalLinkClickListener();
    const { showNavigation, setShowNavigation } = useNavigationToggleState();

    return (
        <Router>
            <CompanyDesignProvider>
                <AppAreaSizeProvider>
                    <AppShell showNavigation={showNavigation} setShowNavigation={setShowNavigation}>
                        <AppRoutes />
                    </AppShell>
                </AppAreaSizeProvider>
            </CompanyDesignProvider>

            <aside id="clippy">
                <ErrorBoundary>
                    <Suspense>
                        <ClippyTrigger />
                    </Suspense>
                </ErrorBoundary>
            </aside>
        </Router>
    );
};

export default App;
